import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../services/api';
import { positionsAPI } from '../services/positionsAPI';
import { User, Position } from '../types';
import PageHeader from '../components/PageHeader';
import DataTable, { Column } from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import AvatarCell from '../components/AvatarCell';
import SidePanel from '../components/SidePanel';
import { API_BASE } from '../utils/fileUtils';
import '../styles/global.css';

const INITIAL_FORM = {
  username: '', email: '', password: '', full_name: '',
  role: 'engineer', city: '',
  position_id: undefined as number | undefined,
  phone_number: '',
  company_start_year: undefined as number | undefined,
  competencies: '',
};

const getRoleText = (role: string) => {
  const map: Record<string,string> = {
    admin:'Администратор', engineering_manager:'Рук. инженеров',
    engineer:'Инженер', guest:'Гость',
  };
  return map[role] || role;
};

const AdminUsers: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editUserIndex, setEditUserIndex] = useState(-1);
  const [positions, setPositions] = useState<Position[]>([]);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  useEffect(() => { loadUsers(); loadPositions(); }, []);

  const loadPositions = async () => {
    try { setPositions(await positionsAPI.getPositions()); }
    catch (err) { console.error(err); }
  };

  const loadUsers = async () => {
    try { setLoading(true); setUsers(await usersAPI.getUsers()); }
    catch (err: any) { setError(err.response?.data?.detail || 'Ошибка загрузки'); }
    finally { setLoading(false); }
  };

  const resetForm = () => { setFormData(INITIAL_FORM); setPhotoFile(null); setPhotoPreview(null); };

  const openCreateForm = () => { setEditingUser(null); resetForm(); setShowForm(true); };

  const openEditForm = (user: User, index?: number) => {
    setEditingUser(user);
    setEditUserIndex(index ?? users.findIndex(u => u.id === user.id));
    setFormData({
      username: user.username, email: user.email, password: '', full_name: user.full_name,
      role: user.role, city: user.city || '', position_id: user.position_id,
      phone_number: user.phone_number || '', company_start_year: user.company_start_year,
      competencies: Array.isArray(user.competencies) ? user.competencies.join(', ') : user.competencies || '',
    });
    setPhotoPreview(user.avatar_path ? `${API_BASE}/uploads/${user.avatar_path}` : null);
    setPhotoFile(null);
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditingUser(null); setEditUserIndex(-1); resetForm(); };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };
  const buildFD = () => {
    const fd = new FormData();
    fd.append('full_name', formData.full_name);
    fd.append('email', formData.email);
    fd.append('role', formData.role);
    if (formData.city) fd.append('city', formData.city);
    if (formData.phone_number) fd.append('phone_number', formData.phone_number);
    if (formData.competencies) fd.append('competencies', formData.competencies);
    if (formData.position_id) fd.append('position_id', String(formData.position_id));
    if (formData.company_start_year) fd.append('company_start_year', String(formData.company_start_year));
    if (photoFile) fd.append('photo', photoFile);
    return fd;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const res = await fetch(`${API_BASE}/users/${editingUser.id}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: buildFD(),
        });
        if (!res.ok) throw new Error((await res.json()).detail || 'Ошибка обновления');
      } else {
        const fd = buildFD();
        fd.append('username', formData.username);
        fd.append('password', formData.password);
        fd.append('role', formData.role);
        if (formData.position_id) fd.append('position_id', String(formData.position_id));
        if (formData.company_start_year) fd.append('company_start_year', String(formData.company_start_year));
        const res = await fetch(`${API_BASE}/users/`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: fd,
        });
        if (!res.ok) throw new Error((await res.json()).detail || 'Ошибка создания');
      }
      closeForm(); loadUsers();
    } catch (err: any) { setError(err.message || 'Ошибка сохранения'); }
  };

  const handleDelete = async (userId: number) => {
    if (!window.confirm('Удалить пользователя?')) return;
    try { await usersAPI.deleteUser(userId); loadUsers(); }
    catch (err: any) { setError(err.response?.data?.detail || 'Ошибка удаления'); }
  };

  const columns: Column<User>[] = [
    {
      key: 'username', header: 'Сотрудник',
      render: (u) => <AvatarCell avatarPath={u.avatar_path} name={u.full_name || u.username} subtitle={`@${u.username}`} />,
    },
    {
      key: 'role', header: 'Роль',
      render: (u) => <StatusBadge status={u.role} label={getRoleText(u.role)} />,
    },
    { key: 'city', header: 'Город', render: (u) => u.city || '—' },
    {
      key: 'is_active', header: 'Статус',
      render: (u) => <StatusBadge status={u.is_active ? 'approved' : 'rejected'} label={u.is_active ? 'Активен' : 'Неактивен'} />,
    },
  ];

  const renderExpandedUser = (u: User) => {
    const competencies = Array.isArray(u.competencies) ? u.competencies.join(', ') : u.competencies || '';
    return (
      <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div className="exp-grid" style={{ flex: 1 }}>
          <div>
            <div className="exp-label">Email</div>
            <div className="exp-value">{u.email}</div>
          </div>
          <div>
            <div className="exp-label">Телефон</div>
            <div className="exp-value">{u.phone_number || '—'}</div>
          </div>
          <div>
            <div className="exp-label">Должность</div>
            <div className="exp-value">
              {u.position ? `${u.position.title} (ур. ${u.position.level})` : '—'}
            </div>
          </div>
          <div>
            <div className="exp-label">В компании с</div>
            <div className="exp-value">{u.company_start_year || '—'}</div>
          </div>
          {competencies && (
            <div style={{ gridColumn: '1 / -1' }}>
              <div className="exp-label">Компетенции</div>
              <div className="exp-value">{competencies}</div>
            </div>
          )}
        </div>
        <div className="exp-actions" onClick={(e) => e.stopPropagation()}>
          <button className="btn btn-primary btn-sm" onClick={() => openEditForm(u, users.indexOf(u))}>Изменить</button>
          {u.id !== currentUser?.id && (
            <button className="btn btn-danger btn-sm" onClick={() => handleDelete(u.id)}>Удалить</button>
          )}
        </div>
      </div>
    );
  };

  if (currentUser?.role !== 'admin') {
    return <div className="container"><div className="error">Нет прав доступа</div></div>;
  }

  if (loading) return <div className="loading">Загрузка...</div>;

  return (
    <div className="container" style={{ paddingTop:'1.5rem', paddingBottom:'2rem' }}>
      <PageHeader
        title="Управление пользователями"
        actions={<button className="btn btn-primary" onClick={openCreateForm}>+ Добавить пользователя</button>}
      />
      {error && <div className="error">{error}</div>}

      <DataTable
        columns={columns}
        data={users}
        rowKey={(u) => u.id}
        emptyText="Нет пользователей"
        minWidth="480px"
        renderExpanded={renderExpandedUser}
      />

      <SidePanel
        isOpen={showForm}
        onClose={closeForm}
        title={editingUser ? 'Редактировать пользователя' : 'Новый пользователь'}
        onPrev={editingUser ? () => openEditForm(users[editUserIndex - 1], editUserIndex - 1) : undefined}
        onNext={editingUser ? () => openEditForm(users[editUserIndex + 1], editUserIndex + 1) : undefined}
        hasPrev={editUserIndex > 0}
        hasNext={editUserIndex < users.length - 1}
        navLabel={editingUser ? `${editUserIndex + 1} из ${users.length}` : undefined}
      >
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2" style={{ gap:'1rem' }}>
            {!editingUser && (
              <>
                <div className="form-group">
                  <label className="form-label">Имя пользователя</label>
                  <input type="text" className="form-control" value={formData.username} onChange={(e) => setFormData({...formData, username:e.target.value})} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Пароль</label>
                  <input type="password" className="form-control" value={formData.password} onChange={(e) => setFormData({...formData, password:e.target.value})} required />
                </div>
              </>
            )}
            <div className="form-group">
              <label className="form-label">Полное имя</label>
              <input type="text" className="form-control" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name:e.target.value})} required />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input type="email" className="form-control" value={formData.email} onChange={(e) => setFormData({...formData, email:e.target.value})} required />
            </div>
            {!editingUser && (
              <div className="form-group">
                <label className="form-label">Роль</label>
                <select className="form-control" value={formData.role} onChange={(e) => setFormData({...formData, role:e.target.value})}>
                  <option value="guest">Гость</option>
                  <option value="engineer">Инженер</option>
                  <option value="engineering_manager">Рук. инженеров</option>
                  <option value="admin">Администратор</option>
                </select>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Должность</label>
              <select className="form-control" value={formData.position_id || ''} onChange={(e) => setFormData({...formData, position_id: e.target.value ? parseInt(e.target.value) : undefined})}>
                <option value="">Без должности</option>
                {positions.map((p) => <option key={p.id} value={p.id}>{p.title} (ур. {p.level})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Город</label>
              <input type="text" className="form-control" value={formData.city} onChange={(e) => setFormData({...formData, city:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Телефон</label>
              <input type="tel" className="form-control" value={formData.phone_number} onChange={(e) => setFormData({...formData, phone_number:e.target.value})} placeholder="701XXXXXXX" />
            </div>
            {!editingUser && (
              <div className="form-group">
                <label className="form-label">Год начала работы</label>
                <input type="number" className="form-control" value={formData.company_start_year || ''} onChange={(e) => setFormData({...formData, company_start_year: e.target.value ? parseInt(e.target.value) : undefined})} min="1950" max={new Date().getFullYear()} placeholder="2020" />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Фото профиля</label>
              <input id="userPhoto" type="file" accept="image/*" onChange={handlePhotoChange} style={{ display:'none' }} />
              <div className="d-flex gap-2" style={{ marginBottom:'0.75rem' }}>
                <button type="button" className="btn-outline-primary" onClick={() => document.getElementById('userPhoto')?.click()}>Выбрать фото</button>
                {photoFile && <button type="button" className="btn-outline-warning ms-2" onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}>Удалить</button>}
              </div>
              {photoPreview && <img src={photoPreview} alt="Preview" style={{ width:120, height:120, objectFit:'cover', borderRadius:8, border:'1px solid var(--border-light)' }} />}
            </div>
            <div className="form-group">
              <label className="form-label">Компетенции</label>
              <textarea className="form-control" value={formData.competencies} onChange={(e) => setFormData({...formData, competencies:e.target.value})} placeholder="Через запятую" rows={3} />
            </div>
          </div>
          <div className="side-panel__footer">
            <button type="button" className="btn btn-secondary" onClick={closeForm}>Отмена</button>
            <button type="submit" className="btn btn-primary">{editingUser ? 'Сохранить' : 'Создать'}</button>
          </div>
        </form>
      </SidePanel>
    </div>
  );
};

export default AdminUsers;
